import React, { useCallback, useState } from 'react';
import styled from 'astroturf/react';
import Modal from 'js/components/modals/Modal';
import { Body } from 'js/design_system/Body';
import { Button } from './design_system/Button';
import StarIcon from 'js/svg/colorless/star.svg';
import CloseIcon from 'js/design_system/icons/close.svg';
import ErrorIcon from 'js/design_system/icons/error.svg';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
  padding: 16px;
`;

const CloseContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
`;

const HeadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const Heading = styled.p`
  @import 'stylesheets/design_system/font';
  @include sans-serif;
  font-size: 24px;
  line-height: 30px;
  text-align: center;
  margin: 0;
  color: $ds-black;
`;

const Subtext = styled(Body)`
  display: flex;
  align-items: center;
  gap: 4px;
  text-align: center;
  color: $ds-gray-dark;
`;

const ResponseContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const StarContainer = styled.div`
  display: flex;
  flex-wrap: nowrap;
  gap: 16px;
`;

const IconButton = styled.button`
  padding: 0;
  border: none;
  background-color: inherit;
  cursor: pointer;
`;

const ButtonContainer = styled.div`
  padding-top: 16px;
  width: 100%;

  @include for-tablet-up {
    width: 80%;
  }

  button {
    width: 100%;
  }
`;

type Props = {
  onClose: () => void;
  onSubmit: (rating: number) => void;
};

export const FeedbackModal: React.FC<Props> = ({ onClose, onSubmit }) => {
  const [rating, setRating] = useState<number>();
  const [showNag, setShowNag] = useState(false);

  const handleSubmit = useCallback(() => {
    if (!rating) {
      setShowNag(true);
    } else {
      onSubmit(rating);
    }
  }, [rating, onSubmit]);

  return (
    <Modal onClose={onClose}>
      <Container>
        <CloseContainer>
          <IconButton onClick={onClose}>
            <CloseIcon size={24} color="#6d6d7d" />
          </IconButton>
        </CloseContainer>

        <HeadingContainer>
          <Heading>How happy are you with our paper recommendations?</Heading>
          <Subtext size="sm">Your response is private</Subtext>
        </HeadingContainer>

        <ResponseContainer>
          <StarContainer>
            {[...Array(5)].map((_item, index) => (
              <IconButton key={index} onClick={() => setRating(index + 1)}>
                <StarIcon
                  size={38}
                  color={index + 1 > (rating ?? 0) ? '#cddaef' : '#386ac1'}
                />
              </IconButton>
            ))}
          </StarContainer>
          {showNag && !rating && (
            <Subtext size="sm">
              <ErrorIcon size={18} />
              Please provide your response
            </Subtext>
          )}
        </ResponseContainer>

        <ButtonContainer>
          <Button size="lg" onClick={handleSubmit}>
            Submit
          </Button>
        </ButtonContainer>
      </Container>
    </Modal>
  );
};
